import React from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import moment from "moment";

const severityFor = (confidence) => {
  if (typeof confidence !== "number") return null;
  if (confidence >= 0.75) return { label: "HIGH", color: "#ef5350" };
  if (confidence >= 0.5) return { label: "MED", color: "#ffa726" };
  return { label: "LOW", color: "#90a4ae" };
};

const ActivityRow = ({ item, sourceColors }) => {
  const severity = severityFor(item.confidence);
  const sourceColor = sourceColors[item.source] || "#90a4ae";
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "72px 100px 1fr 90px",
        gap: 1.5,
        alignItems: "center",
        py: 1.25,
        px: 1.5,
        borderRadius: 1,
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
        },
      }}
    >
      <Box>
        {severity ? (
          <Chip
            label={severity.label}
            size="small"
            sx={{
              backgroundColor: alpha(severity.color, 0.18),
              color: severity.color,
              fontWeight: 700,
              letterSpacing: "0.06em",
              fontSize: "0.65rem",
              height: 20,
            }}
          />
        ) : (
          <Chip
            label="—"
            size="small"
            sx={{ backgroundColor: "transparent", color: "text.disabled", height: 20 }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: sourceColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.sourceLabel}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {item.url ? (
          <MuiLink
            href={item.url}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            color="text.primary"
            sx={{
              fontSize: "0.875rem",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </MuiLink>
        ) : (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </Typography>
        )}
        {item.subtitle && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.subtitle}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right" }}>
        {item.created_at ? moment(item.created_at).fromNow() : ""}
      </Typography>
    </Box>
  );
};

const ActivityFeed = ({ items, sourceColors, emptyMessage = "No recent activity." }) => {
  return (
    <Card sx={{ p: 0 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Recent Activity</Typography>
        <Typography variant="caption" color="text.disabled">
          {items.length} item{items.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Divider />
      {items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.disabled">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider />} sx={{ p: 1 }}>
          {items.map((it, i) => (
            <ActivityRow key={`${it.source}-${it.id || i}`} item={it} sourceColors={sourceColors} />
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default ActivityFeed;
